import React from 'react';
import classNames from 'classnames/bind';
import ReactMarkdown from 'react-markdown';

import { MarkdownWidget as MarkdownWidgetType } from 'types/dashboard_widget';

import {
  customMarkdownAnchor,
  parseEmbeds,
  MarkdownEmbedRegistry,
} from 'services/markdown';

import { Widget } from 'dashboards/widgets/Widget/Widget';
import { PipelineTemplateSmallCardList } from 'analytics-library/components/PipelineTemplateSmallCard/PipelineTemplateSmallCardList';

import { DeployButton } from 'components/DeployButton/DeployButton';

import styles from './MarkdownWidget.module.scss';

/**
 * Registry of custom embed components for the Markdown widget.
 * Use syntax ::embed-type[id]{params} in markdown content.
 * Examples:
 *   ::pipelinetemplates[550e8400-e29b-41d4-a716-446655440000]  (by UUID)
 *   ::pipelinetemplates[traffic]  (by search query)
 *   ::deploybutton[]
 */
const markdownEmbedRegistry: MarkdownEmbedRegistry = {
  pipelinetemplates: PipelineTemplateSmallCardList,
  deploybutton: DeployButton,
};

const c = classNames.bind(styles);

type ContentSegment =
  | { type: 'markdown'; content: string }
  | {
      type: 'embed';
      embedType: string;
      id: string;
      params: Record<string, string>;
    };

function parseContent(content: string): ContentSegment[] {
  const embeds = parseEmbeds(content);

  if (embeds.length === 0) {
    return [{ type: 'markdown', content }];
  }

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const embed of embeds) {
    const embedIndex = content.indexOf(embed.fullMatch, lastIndex);

    // Add markdown content before this embed
    if (embedIndex > lastIndex) {
      const markdownContent = content.slice(lastIndex, embedIndex);
      if (markdownContent.trim()) {
        segments.push({ type: 'markdown', content: markdownContent });
      }
    }

    // Add the embed
    segments.push({
      type: 'embed',
      embedType: embed.type,
      id: embed.id,
      params: embed.params,
    });

    lastIndex = embedIndex + embed.fullMatch.length;
  }

  // Add remaining markdown content after the last embed
  if (lastIndex < content.length) {
    const remainingContent = content.slice(lastIndex);
    if (remainingContent.trim()) {
      segments.push({ type: 'markdown', content: remainingContent });
    }
  }

  return segments;
}

export function MarkdownWidget(widget: MarkdownWidgetType) {
  const segments = React.useMemo(
    () => parseContent(widget.content),
    [widget.content]
  );

  return (
    <Widget widget={widget}>
      <div className="h-full overflow-auto p-4">
        <div className={c('markdown')}>
          {segments.map((segment, index) => {
            if (segment.type === 'markdown') {
              return (
                <ReactMarkdown
                  key={index}
                  components={{ a: customMarkdownAnchor }}
                >
                  {segment.content}
                </ReactMarkdown>
              );
            }

            const EmbedComponent = markdownEmbedRegistry[segment.embedType];

            if (!EmbedComponent) {
              return (
                <div key={index} className={c('unknown-embed')}>
                  Unknown embed type: {segment.embedType}
                </div>
              );
            }

            return (
              <EmbedComponent
                key={index}
                id={segment.id}
                params={segment.params}
              />
            );
          })}
        </div>
      </div>
    </Widget>
  );
}
